/**
 * PrivacyDataCard — self-service consent withdrawal + data deletion.
 *
 * Presents 3 buttons per the framework spec (2026-02):
 *   1. Withdraw cohort contribution — removes user's aggregated rows
 *      from the benchmark library going forward.
 *   2. Withdraw marketing consent — stops product/marketing emails.
 *   3. Delete my data — soft-deletes analyses, purges cohort rows,
 *      anonymises the consent log. Returns a receipt.
 *
 * The required "profile_analysis" consent can only be withdrawn by
 * deleting the account (users can't run any new audit without it, so
 * offering a partial withdrawal is misleading).
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ShieldOff, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';

export function PrivacyDataCard() {
  const [busy, setBusy] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const withdraw = async (purpose) => {
    setBusy(purpose);
    try {
      const { data } = await api.post('/consent/withdraw', { purpose });
      toast.success(`Withdrew consent for "${purpose}".${data.cohort_rows_purged ? ` Purged ${data.cohort_rows_purged} cohort rows.` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.message || 'Failed to withdraw consent');
    } finally {
      setBusy(null);
    }
  };

  const deleteAll = async () => {
    setBusy('delete');
    try {
      const { data } = await api.post('/data/delete');
      setReceipt(data.receipt);
      toast.success('Deletion complete. Receipt below.');
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.message || 'Deletion failed');
    } finally {
      setBusy(null);
      setConfirmDelete(false);
    }
  };

  return (
    <Card className="border-border" data-testid="settings-privacy-data-card">
      <CardContent className="p-6 space-y-5">
        <div>
          <h2 className="text-lg font-semibold">Privacy &amp; data</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Withdraw specific consents or request full deletion of your data. Withdrawals apply immediately; deletion soft-deletes your audits and purges your cohort contributions.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-4">
            <div className="min-w-0">
              <p className="text-[13.5px] font-medium">Withdraw cohort contribution</p>
              <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                Removes your de-identified attributes from the peer benchmark library.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => withdraw('cohort')}
              disabled={busy === 'cohort'}
              data-testid="settings-withdraw-cohort"
            >
              {busy === 'cohort' ? <Loader2 size={14} className="animate-spin" /> : <><ShieldOff size={14} className="mr-1.5" /> Withdraw</>}
            </Button>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-4">
            <div className="min-w-0">
              <p className="text-[13.5px] font-medium">Withdraw marketing consent</p>
              <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                Stops product updates, tips and offers from Blayzrr.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => withdraw('marketing')}
              disabled={busy === 'marketing'}
              data-testid="settings-withdraw-marketing"
            >
              {busy === 'marketing' ? <Loader2 size={14} className="animate-spin" /> : <><ShieldOff size={14} className="mr-1.5" /> Withdraw</>}
            </Button>
          </div>

          <div className="rounded-xl border border-destructive/40 bg-destructive/[0.03] p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-medium text-destructive">Delete my data</p>
                <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                  Soft-deletes every audit you&rsquo;ve run, purges your cohort contributions, and anonymises your consent log. Cannot be undone. Your login credentials remain so you can re-audit later.
                </p>
              </div>
            </div>
            {!confirmDelete ? (
              <div className="mt-3 flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy === 'delete'}
                  data-testid="settings-delete-my-data"
                >
                  <Trash2 size={14} className="mr-1.5" /> Delete my data
                </Button>
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)} disabled={busy === 'delete'} data-testid="settings-delete-cancel">
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={deleteAll}
                  disabled={busy === 'delete'}
                  data-testid="settings-delete-confirm"
                >
                  {busy === 'delete' ? <Loader2 size={14} className="animate-spin" /> : <>I understand — delete everything</>}
                </Button>
              </div>
            )}
          </div>
        </div>

        {receipt && (
          <div className="rounded-xl border border-border bg-secondary/40 p-4 text-[12.5px] leading-relaxed" data-testid="settings-deletion-receipt">
            <p className="font-mono-edge text-[10.5px] tracking-[0.22em] uppercase text-muted-foreground mb-2">Deletion receipt</p>
            <p><strong>{receipt.analyses_soft_deleted}</strong> audit(s) soft-deleted.</p>
            <p><strong>{receipt.cohort_rows_purged}</strong> cohort row(s) purged.</p>
            <p><strong>{receipt.consent_rows_anonymised}</strong> consent log row(s) anonymised.</p>
            <p className="text-muted-foreground text-[11px] mt-1">Ref: {receipt.id?.slice(0, 8)} · {new Date(receipt.created_at).toLocaleString()}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PrivacyDataCard;
